import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import Modal from '@/components/base/Modal';
import GrupModal from './components/GrupModal';
import GrupDetayModal from './components/GrupDetayModal';

interface GroupRow {
  id: string;
  name: string;
  level: string | null;
  day: string | null;
  day_note: string | null;
  time: string | null;
  whatsapp_link: string | null;
  color: string | null;
  active: boolean;
  created_at: string;
}

interface StudentRow {
  id: string;
  group_id: string | null;
  level: string | null;
  parent_name: string | null;
  parent_phone: string | null;
}

interface ProfileRow {
  id: string;
  full_name: string;
  phone: string | null;
}

export default function Gruplar() {
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editGroup, setEditGroup] = useState<GroupRow | null>(null);
  const [detailGroup, setDetailGroup] = useState<GroupRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<GroupRow | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        { data: groupsData, error: groupsError },
        { data: studentsData, error: studentsError },
        { data: profilesData, error: profilesError },
      ] = await Promise.all([
        supabase.from('zehrahoca_groups').select('*').order('created_at', { ascending: true }),
        supabase.from('zehrahoca_students').select('id, group_id, level, parent_name, parent_phone'),
        supabase.from('zehrahoca_profiles').select('id, full_name, phone').eq('role', 'ogrenci'),
      ]);
      if (groupsError) throw groupsError;
      if (studentsError) throw studentsError;
      if (profilesError) throw profilesError;

      setGroups((groupsData ?? []) as GroupRow[]);
      setStudents((studentsData ?? []) as StudentRow[]);
      setProfiles((profilesData ?? []) as ProfileRow[]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const handleChange = () => fetchAll();
    window.addEventListener('zehrahoca:groups-changed', handleChange);
    return () => window.removeEventListener('zehrahoca:groups-changed', handleChange);
  }, [fetchAll]);

  const profileMap = useMemo(() => {
    const m: Record<string, ProfileRow> = {};
    profiles.forEach((p) => { m[p.id] = p; });
    return m;
  }, [profiles]);

  const studentCountByGroup = useMemo(() => {
    const m: Record<string, number> = {};
    students.forEach((s) => {
      if (s.group_id) m[s.group_id] = (m[s.group_id] ?? 0) + 1;
    });
    return m;
  }, [students]);

  function handleNew() {
    setEditGroup(null);
    setModalOpen(true);
  }

  function handleEdit(g: GroupRow) {
    setEditGroup(g);
    setModalOpen(true);
  }

  function openDetail(g: GroupRow) {
    setDetailGroup(g);
  }

  async function doDelete() {
    if (!confirmDelete) return;
    const groupId = confirmDelete.id;
    try {
      // Öğrencilerin grup bağlantısını kaldır
      await supabase.from('zehrahoca_students').update({ group_id: null }).eq('group_id', groupId);

      // Gruba bağlı ödevlerin teslimlerini ve ödevleri sil
      const { data: groupAssignments } = await supabase
        .from('zehrahoca_assignments')
        .select('id')
        .eq('group_id', groupId);
      const assignmentIds = (groupAssignments ?? []).map((a: { id: string }) => a.id);
      if (assignmentIds.length > 0) {
        await supabase.from('zehrahoca_assignment_submissions').delete().in('assignment_id', assignmentIds);
      }
      await supabase.from('zehrahoca_assignments').delete().eq('group_id', groupId);

      // Gruba bağlı doküman, kayıt, duyuru ve dersleri sil
      await supabase.from('zehrahoca_documents').delete().eq('group_id', groupId);
      await supabase.from('zehrahoca_lesson_records').delete().eq('group_id', groupId);
      await supabase.from('zehrahoca_announcements').delete().eq('group_id', groupId);
      await supabase.from('zehrahoca_lessons').delete().eq('group_id', groupId);

      // Son olarak grubu sil
      const { error: delError } = await supabase.from('zehrahoca_groups').delete().eq('id', groupId);
      if (delError) throw delError;

      setConfirmDelete(null);
      window.dispatchEvent(new Event('zehrahoca:groups-changed'));
    } catch (err) {
      setError((err as Error).message || 'Silme işlemi başarısız oldu.');
      setConfirmDelete(null);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        <p className="mt-4 text-sm text-foreground-500">Gruplar yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl py-10">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
          <p className="mt-2 text-sm font-medium text-red-700">Gruplar yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button type="button" onClick={fetchAll} className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer">
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Sınıf / Grup Yönetimi</h1>
          <p className="mt-1 text-sm text-foreground-600">Grupları oluşturun, ders günlerini ve öğrencilerini yönetin.</p>
        </div>
        <button type="button" onClick={handleNew} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer">
          <i className="ri-add-line text-base" /> Yeni Grup Ekle
        </button>
      </div>

      {groups.length === 0 ? (
        <div className="rounded-xl border border-background-200 bg-white p-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
            <i className="ri-team-line text-2xl text-foreground-400" />
          </div>
          <p className="text-sm font-semibold text-foreground-700">Henüz grup bulunmuyor.</p>
          <p className="mt-1 text-xs text-foreground-500">İlk grubunuzu oluşturarak başlayın.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((g) => {
            const count = studentCountByGroup[g.id] ?? 0;
            return (
              <div
                key={g.id}
                className={`group relative flex flex-col rounded-xl border bg-white p-5 transition hover:shadow-md cursor-pointer ${g.active ? 'border-background-200' : 'border-background-200 opacity-70'}`}
                onClick={() => openDetail(g)}
              >
                <div className="mb-3 flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="flex h-11 w-11 items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${g.color ?? '#6366f1'}20`, color: g.color ?? '#6366f1' }}
                    >
                      <i className="ri-team-line text-xl" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground-950 text-sm leading-tight">{g.name}</h3>
                      {g.level && <p className="text-xs text-foreground-500">{g.level}</p>}
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${g.active ? 'bg-emerald-50 text-emerald-600' : 'bg-background-100 text-foreground-500'}`}
                  >
                    <i className={g.active ? 'ri-checkbox-circle-fill' : 'ri-pause-circle-line'} />
                    {g.active ? 'Aktif' : 'Pasif'}
                  </span>
                </div>

                <div className="mb-4 space-y-1.5 text-xs text-foreground-600">
                  {(g.day || g.time) && (
                    <p className="flex items-center gap-2">
                      <i className="ri-calendar-2-line text-foreground-400" />
                      {g.day}{g.day && g.time ? ' · ' : ''}{g.time}
                    </p>
                  )}
                  {g.day_note && (
                    <p className="flex items-center gap-2 text-foreground-500">
                      <i className="ri-information-line text-foreground-400" />
                      {g.day_note}
                    </p>
                  )}
                  <p className="flex items-center gap-2">
                    <i className="ri-user-line text-foreground-400" />
                    {count} öğrenci
                  </p>
                  {g.whatsapp_link && (
                    <p className="flex items-center gap-2">
                      <i className="ri-whatsapp-line text-emerald-500" />
                      <span className="truncate">WhatsApp grubu bağlı</span>
                    </p>
                  )}
                </div>

                <div className="mt-auto flex items-center justify-between border-t border-background-100 pt-3">
                  <button
                    type="button"
                    onClick={() => openDetail(g)}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-background-300 bg-white px-3 py-1.5 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer"
                  >
                    <i className="ri-eye-line" /> Grup Detayı
                  </button>
                  <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => handleEdit(g)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-background-100 text-foreground-600 transition hover:bg-background-200"
                      title="Düzenle"
                    >
                      <i className="ri-edit-line text-base" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(g)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500 transition hover:bg-red-100"
                      title="Sil"
                    >
                      <i className="ri-delete-bin-line text-base" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <GrupModal open={modalOpen} onClose={() => setModalOpen(false)} group={editGroup} />

      <GrupDetayModal
        group={detailGroup}
        onClose={() => setDetailGroup(null)}
      />

      {/* Silme onay modalı */}
      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Grubu Sil"
        subtitle="Bu işlem geri alınamaz."
      >
        <div className="space-y-5">
          <p className="text-sm text-foreground-700">
            <span className="font-semibold">{confirmDelete?.name}</span> grubunu silmek istediğinize emin misiniz?
            Bu gruptaki öğrencilerin grup bağlantısı kaldırılır.
          </p>
          <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
            <button
              type="button"
              onClick={() => setConfirmDelete(null)}
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={doDelete}
              className="rounded-lg bg-red-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-600 cursor-pointer whitespace-nowrap"
            >
              Sil
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}